// Can this user work in this project? — asked as a probe, not as a side effect
// of minting a session.
//
// The Focus mode picker refuses a project the signed-in user cannot use: below
// Developer on GitLab, or (where Loom's own access gate is enforcing) with no
// grant on it. That refusal is an ACCESS question, and `GET
// /api/projects/:projectId/access` is the content-free route that answers it —
// ids, a boolean and a level, no pipeline, no session, no cost.
//
// The two legs are two DIFFERENT facts, and the screen has to say which one it
// hit. Live, a user who held Developer on GitLab and only lacked Loom's own
// per-project grant was told to go ask GitLab for a role they already had —
// a wrong errand, printed with full confidence. So the probe answers with a
// REASON, not a boolean, and the copy hangs off that reason.
//
// A probe that cannot answer must NOT deny. A 503 (membership unverifiable) or
// a 500 resolves to `unavailable`, which the picker treats as "carry on": a
// transient blip locking a project out is the over-reach the picker's own
// comment warns against. 401 stays `loading` — the house convention (`useAccess`,
// `useIssueDetail`, four more): the route loader bounces to /sign-in and a
// half-rendered refusal underneath it is noise.

import { useCallback, useEffect, useState } from "react";
import { z } from "zod";

import type { MessageKey } from "../i18n";
import { UnauthorizedError, httpGet } from "../lib/http";

const ProjectAccessSchema = z.object({
  gitlabDeveloper: z.boolean(),
  loomLevel: z.string().nullable(),
  accessEnforced: z.boolean(),
});

export type ProjectAccessBody = z.infer<typeof ProjectAccessSchema>;

/**
 * WHY a project is refused.
 *
 *  • `gitlab`  — below Developer on the GitLab project. A GitLab errand.
 *  • `loom`    — GitLab is fine; Loom's own per-project grant is missing. A
 *                Loom errand, and the one the old single sentence lied about.
 *  • `both`    — neither holds.
 *  • `unknown` — a 403 from `POST /sessions`, which proves a denial without
 *                saying which leg produced it (GitLab pre-flight and the Loom
 *                gate both answer 403 there).
 */
export type AccessDenialReason = "gitlab" | "loom" | "both" | "unknown";

/**
 * The reason mapping, kept apart from the hook so both the probe and the
 * session-mint leg resolve it the same way — and so it is testable without a
 * render. `null` means "not refused".
 */
export const denialReasonOf = (
  body: ProjectAccessBody,
): AccessDenialReason | null => {
  const gitlabMissing = body.gitlabDeveloper === false;
  // A null level only means "no grant" while the gate is enforcing; with
  // enforcement off every project is open and the level carries no refusal.
  const loomMissing = body.accessEnforced && body.loomLevel === null;
  if (gitlabMissing && loomMissing) return "both";
  if (gitlabMissing) return "gitlab";
  if (loomMissing) return "loom";
  return null;
};

/**
 * Reason → the sentence under the "access denied" headline. `both` and
 * `unknown` share one line on purpose: it states the consequence and names one
 * person to ask, rather than handing the reader two chores or a leg it cannot
 * prove.
 */
export const accessDeniedHelpKey = (
  reason: AccessDenialReason,
): MessageKey => {
  switch (reason) {
    case "gitlab":
      return "focus.session.accessDenied.gitlab";
    case "loom":
      return "focus.session.accessDenied.loom";
    default:
      return "focus.session.accessDenied.either";
  }
};

export type ProjectAccessState =
  | { readonly kind: "loading" }
  | { readonly kind: "ready"; readonly denial: AccessDenialReason | null }
  | { readonly kind: "unavailable" };

export interface ProjectAccess {
  readonly state: ProjectAccessState;
  /** Re-run the probe. Offered next to the picker's own retry. */
  readonly retry: () => void;
}

export const useProjectAccess = (projectId: number): ProjectAccess => {
  const [state, setState] = useState<ProjectAccessState>({ kind: "loading" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    httpGet(`/api/projects/${projectId}/access`, ProjectAccessSchema)
      .then((body) => {
        if (cancelled) return;
        setState({ kind: "ready", denial: denialReasonOf(body) });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // 401 is the route loader's business; anything else that reached here
        // is a probe that could not answer, and an unanswered probe denies
        // nothing.
        setState(
          err instanceof UnauthorizedError
            ? { kind: "loading" }
            : { kind: "unavailable" },
        );
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return { state, retry };
};
